import React, { FC, useRef, useState } from 'react';

import { FormattedMessage } from 'react-intl';
import { useTheme } from 'styled-components';

import { BaseStyleEnums } from '@nhl/nhl-breakaway-ui';
import Headshot from '@nhl/nhl-react-ui/lib/Headshot';
import headshotTypes from '@nhl/nhl-react-ui/lib/Headshot/headshotTypes';
import { useScroll } from '@nhl/react-hooks/lib/useScroll';
import { useSize } from '@nhl/react-hooks/lib/useSize';

import { LeaderItem } from '~/core/types';
import { commonMessages } from '~/utility/commonMessages';
import { getPlayerUrl } from '~/views/components/tableCells/playerNameCell/PlayerNameCell';

import {
    ImgContainer,
    NameContainerLarge,
    NameContainerSmall,
    PlayerContainerLarge,
    PlayerContainerSmall,
    PlayerDetailContainer,
    PlayerInfoLarge,
    PlayerInfoSmall,
    PlayerNameDetailContainerSmall,
    StatCategoryName,
    StatCategoryValue,
    StatDetails,
    TeamIcon,
} from './styles';

const DIVIDER = ' • ';
const GOALIE_POSITION_CODE = 'G';
const SCREEN_RESIZE_THROTTLE = 100;
const STICKY_HEIGHT = 150;

type LeaderSpotlightProps = {
    categoryLabel: string;
    leader: LeaderItem;
    seasonId?: number;
    value: number | string;
};

const LeaderSpotlight: FC<LeaderSpotlightProps> = ({ categoryLabel, leader, seasonId, value }) => {
    const theme = useTheme();
    const screenSize = useSize(SCREEN_RESIZE_THROTTLE);
    const isLessThanMediumScreen = screenSize.width < BaseStyleEnums.ScreenWidthBreakpoints.medium;

    const leaderSpotlightRef = useRef<HTMLDivElement | null>(null);
    const [isFixed, setIsFixed] = useState(false);

    useScroll({
        onScrollChanged: () => {
            const top = leaderSpotlightRef.current?.getBoundingClientRect().top || Infinity;
            setIsFixed(top <= STICKY_HEIGHT);
        },
    });

    const playerHeadshotUrl = `${window.__BASE_EXTERNAL_ASSETS_URL__}/mugs/nhl/${seasonId}/${leader.team.rawTricode}/${leader.player.id}.png`;
    const headshot = (
        <ImgContainer>
            <Headshot
                type={leader.player.positionCode === GOALIE_POSITION_CODE ? headshotTypes.GOALIE : headshotTypes.SKATER}
                urlOverride={playerHeadshotUrl}
                size={isLessThanMediumScreen ? 56 : 128}
                backgroundColor={theme.Surface.secondary}
                alt={leader.player.fullName}
            />
        </ImgContainer>
    );

    const playerDetails = (
        <PlayerDetailContainer>
            <TeamIcon team={leader.team} season={seasonId} displayName={false} useInTable={true} />
            {leader.team.triCode}
            {DIVIDER}#{leader.player.sweaterNumber}
            {DIVIDER}
            {leader.player.positionCode}
        </PlayerDetailContainer>
    );

    const statDetails = (
        <StatDetails>
            <StatCategoryName>
                <FormattedMessage {...commonMessages.labels[categoryLabel]} />
            </StatCategoryName>
            <StatCategoryValue>{value}</StatCategoryValue>
        </StatDetails>
    );

    return isLessThanMediumScreen ? (
        <PlayerContainerSmall ref={leaderSpotlightRef} $isFixed={isFixed}>
            <PlayerInfoSmall href={`${window.location.origin}/player/${leader.player.id}`}>
                {headshot}
                <PlayerNameDetailContainerSmall>
                    <NameContainerSmall>{leader.player.fullName}</NameContainerSmall>
                    {playerDetails}
                </PlayerNameDetailContainerSmall>
            </PlayerInfoSmall>
            {statDetails}
        </PlayerContainerSmall>
    ) : (
        <PlayerContainerLarge>
            <PlayerInfoLarge href={getPlayerUrl(leader.player.id)}>
                {headshot}
                <NameContainerLarge>
                    <div>{leader.player.firstName}</div>
                    <div>{leader.player.lastName}</div>
                </NameContainerLarge>
                {playerDetails}
            </PlayerInfoLarge>
            {statDetails}
        </PlayerContainerLarge>
    );
};

export default LeaderSpotlight;
